// SYNCED FROM: VidaReactNative/src/features/AddressVerification/hooks/useAddressVerification.ts
// Keep in sync. See SYNC.md. The verify() body is byte-identical to VRN; the only
// divergences are the imports (repo paths) and a single line — where VRN reads
//   const verificationEnabled = useFeatureFlag('rn-address-verification') === true;
// onboarding hardcodes `true` (always on; the member-contact screen is already
// gated by the member_contact__collect_in_onboarding experiment, and onboarding
// has no rn-address-verification flag). When syncing, port everything except
// that one line.
import { useCallback, useRef, useState } from 'react';

import { analytics } from '@/modules/analytics';

import { verifyUsAddress, type LobDeliverability, type LobVerifyResponse } from '../api/lobApi';
import type {
  Address,
  DialogState,
  Surface,
  UnitDeliverabilityWarning,
  VerifyResult,
} from '../types';
import { buildVerifyRequest } from '../utils/buildVerifyRequest';
import { areAddressesEqual } from '../utils/compareAddresses';
import { parseVerifyResponse } from '../utils/parseVerifyResponse';
import { trimAddress } from '../utils/trimAddress';

type UseAddressVerificationOptions = {
  surface: Surface;
};

type VerifyController = {
  /**
   * Run Lob verification on the (trimmed) input address. Drives `dialogState`;
   * the consumer renders the dispatcher (`<VerificationDialogs />`) to mount
   * the active dialog. Promise resolves when the user makes a final decision.
   * `ok: true` means proceed with `address`;
   * `ok: false` means the caller should abort the save.
   *
   * If invoked while a verification is already in flight (e.g. double-tap), the
   * second call resolves immediately to `{ ok: false }` — the in-flight verify owns
   * the user's attention until it settles. This keeps consumers from running their
   * post-verify side effects twice.
   */
  verify: (input: Address) => Promise<VerifyResult>;
  /**
   * Discriminated state for the verification dialog. Pass to `<VerificationDialogs />`
   * to render the active dialog in the consumer's tree.
   */
  dialogState: DialogState;
  /** True while a Lob request is in flight, but not while a dialog is showing */
  isVerifying: boolean;
};

const isUnitWarningBucket = (
  deliverability: LobDeliverability
): deliverability is UnitDeliverabilityWarning =>
  deliverability === 'deliverable_missing_unit' ||
  deliverability === 'deliverable_unnecessary_unit' ||
  deliverability === 'deliverable_incorrect_unit';

export const useAddressVerification = (
  options: UseAddressVerificationOptions
): VerifyController => {
  const { surface } = options;
  // DIVERGENCE FROM VRN (see banner): VRN reads useFeatureFlag('rn-address-verification').
  const verificationEnabled = true;
  const [dialogState, setDialogState] = useState<DialogState>({ kind: 'idle' });
  const [isVerifying, setIsVerifying] = useState(false);
  const inFlightRef = useRef(false);

  const verify = useCallback(
    async (rawInput: Address): Promise<VerifyResult> => {
      if (inFlightRef.current) return { ok: false };

      const trimmed = trimAddress(rawInput);

      if (!verificationEnabled) {
        analytics.trackEvent('address_verify_skipped', { surface, reason: 'flag_off' });
        return { ok: true, address: trimmed, changed: false };
      }

      inFlightRef.current = true;
      analytics.trackEvent('address_verify_attempted', {
        surface,
        has_unit: !!trimmed.addressLine2,
      });
      setIsVerifying(true);

      try {
        let response: LobVerifyResponse;
        try {
          response = await verifyUsAddress(buildVerifyRequest(trimmed)).finally(() =>
            setIsVerifying(false)
          );
        } catch (error) {
          const errorObj = error instanceof Error ? error : new Error(String(error));
          analytics.trackEvent('address_verify_failed', {
            surface,
            error_message: errorObj.message,
          });
          analytics.trackError(errorObj, { surface, component: 'useAddressVerification' });
          // `await` is load-bearing on every dialog-wait in this try block: the
          // outer try/finally clears `dialogState` once verify() settles, so we
          // must hold the promise here (not just return it) to keep the dialog
          // mounted until the user clicks.
          return await new Promise<VerifyResult>((resolve) => {
            setDialogState({
              kind: 'error',
              address: trimmed,
              error: errorObj,
              onSubmitAnyway: () => {
                analytics.trackEvent('address_verify_user_submitted_after_error', { surface });
                resolve({ ok: true, address: trimmed, changed: false });
              },
              onClose: () => {
                analytics.trackEvent('address_verify_user_cancelled', { surface });
                resolve({ ok: false });
              },
            });
          });
        }

        const verified = parseVerifyResponse(response);
        const deliverability = response.deliverability;

        if (deliverability === 'undeliverable') {
          const footnoteCodes = response.deliverability_analysis?.dpv_footnotes ?? [];
          analytics.trackEvent('address_verify_undeliverable', {
            surface,
            footnotes: footnoteCodes,
          });
          return await new Promise<VerifyResult>((resolve) => {
            setDialogState({
              kind: 'undeliverable',
              address: trimmed,
              footnoteCodes,
              onSubmitAnyway: () => {
                analytics.trackEvent('address_verify_user_submitted_undeliverable', { surface });
                resolve({ ok: true, address: trimmed, changed: false });
              },
              onClose: () => {
                analytics.trackEvent('address_verify_user_cancelled', { surface });
                resolve({ ok: false });
              },
            });
          });
        }

        if (isUnitWarningBucket(deliverability)) {
          analytics.trackEvent('address_verify_warning', { surface, deliverability });
          const ack = await new Promise<'continue' | 'cancel'>((resolve) => {
            setDialogState({
              kind: 'warning',
              address: trimmed,
              deliverability,
              onContinue: () => {
                analytics.trackEvent('address_verify_user_acknowledged_warning', { surface });
                resolve('continue');
              },
              onCancel: () => {
                analytics.trackEvent('address_verify_user_cancelled', { surface });
                resolve('cancel');
              },
            });
          });
          if (ack === 'cancel') return { ok: false };
        }

        if (!areAddressesEqual(trimmed, verified)) {
          analytics.trackEvent('address_verify_changed', { surface });
          return await new Promise<VerifyResult>((resolve) => {
            setDialogState({
              kind: 'confirm',
              user: trimmed,
              verified,
              deliverability,
              onUseVerified: () => {
                analytics.trackEvent('address_verify_user_accepted_verified', { surface });
                analytics.trackEvent('address_verify_succeeded', { surface, deliverability });
                resolve({ ok: true, address: verified, changed: true });
              },
              onUseEntered: () => {
                analytics.trackEvent('address_verify_user_declined_suggestion', { surface });
                analytics.trackEvent('address_verify_succeeded', { surface, deliverability });
                resolve({ ok: true, address: trimmed, changed: false });
              },
              onCancel: () => {
                analytics.trackEvent('address_verify_user_cancelled', { surface });
                resolve({ ok: false });
              },
            });
          });
        }

        analytics.trackEvent('address_verify_succeeded', { surface, deliverability });
        return { ok: true, address: trimmed, changed: false };
      } finally {
        setDialogState({ kind: 'idle' });
        inFlightRef.current = false;
      }
    },
    [surface, verificationEnabled]
  );

  return { verify, dialogState, isVerifying };
};
